import React, { useMemo, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads';
import {
	getAnnotationThreadKeys,
	onlyHasReactionsSummaryErrors,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import {
	useCommentsData,
	type CommentsDataMap as LegacyCommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { fg } from '@confluence/feature-gating';
import {
	handleCommentsPanelNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import { usePageContentId } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { SIDEBAR_COMMENTS_PANEL_LOAD_DELETED_CONTENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { expVal } from '@confluence/feature-experiments';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';

import { Loading } from '../components/Loading';
import { ErrorPanel } from '../components/ErrorPanel';
import { EmptyStateComponent } from '../components/EmptyStateComponent';
import { EmptyOpenViewSVG } from '../assets/EmptyOpenView';
import { CommentsPanelList } from '../components/CommentsPanelList';
import { useCommentsPanelMetrics } from '../hooks/useCommentsPanelMetrics';
import { COMMENTS_PANEL_DELETED_CONTENT_VIEW_METRIC } from '../perf.config';

import {
	DEFAULT_TOP_LEVEL_ACTIONS,
	DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE,
} from './topLevelActions';

const i18n = defineMessages({
	emptyCommentsHeaderText: {
		id: 'comments-panel.empty.deleted.content.comments.header.text',
		defaultMessage: 'No comments on deleted content',
		description:
			'Header text to display in Deleted Content View empty state when there are no open comments on the page.',
	},
	emptyCommentsBodyText: {
		id: 'comments-panel.empty.deleted.content.comments.body.text',
		defaultMessage: 'Comments attached to deleted content will be saved here.',
		description:
			'Body text to display in Deleted Content View empty state when there are no Open comments on the page.',
	},
});

type DeletedContentViewProps = {
	showDeleteOption: boolean;
	flags?: FlagsStateContainer;
};

export const DeletedContentView = ({ showDeleteOption, flags }: DeletedContentViewProps) => {
	const { formatMessage } = useIntl();
	const pageMode = useGetPageMode();
	const [contentId] = usePageContentId();
	const { contentStatus } = useContentStatus();
	const [
		{
			initialDataLoadedForViewMap,
			showInlineComments,
			currentView,
			currentlySelectedThreadKey,
			commentType,
			commentContainerToFocus,
		},
		{ setInitialDataLoadedForView },
	] = useCommentsPanel();

	const [
		{ orderedActiveAnnotationIdList, commentsDataMap: legacyCommentsDataMap, removedThreadsMap },
		{ clearRemovedComments },
	] = useCommentsData();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const [allCommentsRendered, setAllCommentsRendered] = useState(false);
	const [commentBatchRendered, setCommentBatchRendered] = useState(false);

	// New comments data manager hooks - only used when experiment is enabled
	const newCommentsDataResult = useAllCommentsData({
		contentId: contentId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(contentStatus),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !contentId,
		},
	});

	// Legacy query hooks - only used when experiment is disabled
	const legacyQueryResult = useActiveCommentsQuery({
		pageId: contentId || '',
		skip: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
	});

	// Use the appropriate data source based on experiment
	const {
		loading: hookDataLoading,
		error,
		refetch,
		data,
	} = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? {
				...newCommentsDataResult,
				data: {
					comments: {
						totalCount: newCommentsDataResult.commentCount,
						nodes: newCommentsDataResult.openComments,
					},
				},
				error: newCommentsDataResult.error as any,
			}
		: legacyQueryResult;

	// Create commentsDataMap from new data when experiment is enabled
	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? (newCommentsDataResult.commentsDataMap as LegacyCommentsDataMap)
		: legacyCommentsDataMap;

	// Enhanced loading logic that accounts for new comments data manager processing
	// For new data manager, show loading until data processing is complete
	const loading = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? hookDataLoading || !newCommentsDataResult.isDataProcessed
		: hookDataLoading;

	useEffect(() => {
		// When this view unmounts, we want to clear out the "resolved"/"deleted" comments
		return () => {
			clearRemovedComments();
		};
	}, [clearRemovedComments]);

	useEffect(() => {
		if (!loading) {
			setInitialDataLoadedForView({ viewToSetLoaded: ViewValues.DELETED_CONTENT });

			// handle focused comment
			if (currentlySelectedThreadKey) {
				if (fg('confluence_comments_unified_navigation')) {
					handleCommentsPanelNavigation({
						scrollBehavior: ScrollBehavior.SMOOTH,
						context: NavigationContext.PANEL,
						block: 'start',
						commentId: commentContainerToFocus,
						annotationId: currentlySelectedThreadKey,
						commentType: commentType as 'inline' | 'footer' | undefined,
						scrollOnLoad: true,
					});
				} else {
					scrollCommentsPanelToThread({
						annotationId: currentlySelectedThreadKey,
						commentType,
						isCommentThreadResolved: false,
						scrollOnLoad: true,
						commentContainerToFocus,
					});
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- ignore change in currentlySelectedThreadKey since we only want to scroll the first time for focused comments
	}, [loading, setInitialDataLoadedForView]);

	const isInitialCommentDataLoaded = initialDataLoadedForViewMap[ViewValues.DELETED_CONTENT];

	const commentThreads = useMemo(() => {
		if (
			(!isInitialCommentDataLoaded && loading) ||
			(orderedActiveAnnotationIdList.length === 0 && Object.keys(removedThreadsMap).length === 0)
		) {
			return [];
		}

		return getVisibleCommentThreads({
			currentView,
			commentsDataMap,
			includeGeneralComments: false,
			includeInlineComments: showInlineComments,
			orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
		});
	}, [
		commentsDataMap,
		currentView,
		isInitialCommentDataLoaded,
		loading,
		orderedActiveAnnotationIdList,
		removedThreadsMap,
		showInlineComments,
	]);

	useCommentsPanelMetrics({
		loading,
		error,
		allCommentsRendered,
		commentBatchRendered,
		totalComments: fg('confluence_frontend_abort_panel_exp_if_no_comment')
			? commentThreads.length
			: data?.comments?.nodes?.length,
		contentId,
		pageMode,
		experienceName: SIDEBAR_COMMENTS_PANEL_LOAD_DELETED_CONTENT_EXPERIENCE,
		metric: COMMENTS_PANEL_DELETED_CONTENT_VIEW_METRIC,
	});

	// When using new data manager, show loading whenever loading is true
	// This prevents showing empty state while data is being processed
	if (loading && expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		return <Loading />;
	}

	if (!isInitialCommentDataLoaded) {
		if (loading) {
			return <Loading />;
		}

		if (error && !onlyHasReactionsSummaryErrors(error)) {
			return (
				<ErrorPanel
					error={error}
					onRetryClick={async () => {
						await refetch();
					}}
				/>
			);
		}
	}

	if (commentThreads.length === 0) {
		return (
			<EmptyStateComponent
				SVG={EmptyOpenViewSVG}
				emptyCommentsHeaderText={formatMessage(i18n.emptyCommentsHeaderText)}
				emptyCommentsBodyText={formatMessage(i18n.emptyCommentsBodyText)}
				onRendered={
					fg('confluence_frontend_abort_panel_exp_if_no_comment')
						? () => {
								setCommentBatchRendered(true);
								setAllCommentsRendered(true);
							}
						: undefined
				}
			/>
		);
	}

	return (
		<CommentsPanelList
			commentThreads={commentThreads}
			supportedTopLevelActions={
				showDeleteOption ? DEFAULT_TOP_LEVEL_ACTIONS : DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE
			}
			flags={flags}
			onAllCommentsRendered={() => setAllCommentsRendered(true)}
			onCommentBatchRendered={() => setCommentBatchRendered(true)}
		/>
	);
};
